import { createContext, useContext, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Brush, Car, Check, Cigarette, Cloud, CloudFog, Cloudy, Coffee, Droplets, Eraser, Flag, Footprints, Hand, Heart, Route, Image as ImageIcon, Loader2, Maximize2, Mountain, RotateCw, ScanSearch, Sparkles, Sprout, Trash2, Undo2, Waves, Wind, X, ZoomIn, ZoomOut, type LucideIcon } from 'lucide-react';
import { LibraryPicker, type PickItem } from '@/components/quicktools/LibraryPicker';
import type { DepthPreview } from '@/lib/quicktools-api';
import { BRUSHES, PAINT_DIR, PAINT_KINDS, PAINT_ROOT, PAINT_TINT, decodePaint, encodePaint, type Brush as BrushSize, type PaintKind, type PaintRegion, type Stroke } from '@/lib/paint-motion';
import LIBRARY from '@/lib/overlay-library.json';
import { cn } from '@/lib/utils';

// THE THREE NEW STEPS OF THE LIVING COVER (owner 2026-10-23, the 7-step path he approved: "we just need to create a
// perfect process ... not overwhelming them ... easy and fun and fast but also they feel like they have creative
// control"): what we found in the picture (they tap only if it is wrong), the crop (three tiles of their own picture:
// none, a new sky, a full cutout for a new background; the sky or the background itself is picked after the song) and what moves (chips already on when they fit, and one button to
// paint something to move). The engine reads what they pick (services/quicktools/living-motion.js).

type LibItem = { group: string; kind: string; en: string; es: string; file: string; preview: string; thumb?: string };
const BACKDROPS = LIBRARY.backdrops as Record<string, LibItem>;
/** The library's skies: its 'sky' and 'space' backdrops (living-motion.js SKIES, the same rule), shown first. */
const SKY_GROUPS = ['sky', 'space'];
export const SKY_IDS = Object.keys(BACKDROPS).filter((id) => SKY_GROUPS.includes(BACKDROPS[id].group));
/**
 * What can go in place of the part painted (owner 2026-10-27: "i wanted to crop out the sky and replace it"; the part
 * may be a wall or water, not only sky): every backdrop, the skies first (living-motion.js motionOf takes them all).
 */
export const REPLACE_IDS = [...SKY_IDS, ...Object.keys(BACKDROPS).filter((id) => !SKY_GROUPS.includes(BACKDROPS[id].group))];
/**
 * WHAT IS IN IT: the first answer (owner 2026-10-26: "object, person/animal, scene"), and for a scene what stands in
 * front of it. Each maps to what the engine reads: subject, objectmotion (float / lean), distance.
 */
export const FOUND_KINDS = ['alive', 'object', 'scene'] as const;
export type FoundKind = (typeof FOUND_KINDS)[number];
type FrontKind = 'alive' | 'object';
const KIND_ICONS: Record<FoundKind, LucideIcon> = { alive: Heart, object: Box, scene: Mountain };
/** The first answer: the one they gave, else the one their subject is in. */
export function foundKind(kind: string, subject: string): FoundKind | null {
  if ((FOUND_KINDS as readonly string[]).includes(kind)) return kind as FoundKind;
  if (subject === 'person' || subject === 'animal') return 'alive';
  if (subject === 'object') return 'object';
  if (subject === 'scene') return 'scene';
  return null;
}
/** What stands in front of a scene: null for just the scene. */
function frontOf(subject: string): FrontKind | null {
  if (subject === 'person' || subject === 'animal') return 'alive';
  if (subject === 'object') return 'object';
  return null;
}
/** Close or far: what they said, else far when the check found them small (under a third of the frame's height). */
export function distanceOf(distance: string, depth: DepthPreview | null): 'close' | 'far' {
  if (distance === 'far' || distance === 'close') return distance;
  const box = depth?.box;
  return box && box[3] - box[1] < 0.33 ? 'far' : 'close';
}
const PAINT_ICONS: Record<PaintKind, LucideIcon> = {
  water: Waves, waterfall: Droplets, river: Route, clouds: Cloudy, fog: CloudFog, smoke: Cloud, steam: Coffee, traffic: Car, flag: Flag, grass: Sprout, swing: Footprints,
};
/**
 * WHAT KIND OF MOTION (owner 2026-10-27: "Step number 5 is a little ridiculous because it lists water, waterfall,
 * river.... than clouds,fog,smoke and steam which would basically all look the same ... just ask what is actually
 * needed to know and that is what and where they want motion and what kind of motion"): four kinds, each the engine's
 * own (living-motion.js PAINT): flowing (one way, turned with the arrow: water, a river, a waterfall, car lights),
 * drifting (clouds, fog, smoke, steam), waving (a flag, cloth) and swaying (grass, trees, plants). Paint where, pick
 * which way. A cover painted with the older kinds keeps them.
 */
export const MOVE_KINDS: PaintKind[] = ['river', 'clouds', 'flag', 'grass'];
// One tap turns the flow a quarter round (down, left, up, right).
const TURN: [number, number][] = [[0, 1], [-1, 0], [0, -1], [1, 0]];

const P = 'qt.tools.canvas.motion';

/**
 * STEP: WHAT IS IN IT (owner 2026-10-26: "when asking what the image contains ... present the three options of object,
 * person/animal, scene.... than if they choose option present them with still or motion, than close or distant... if
 * they select person/animal present them with standing or sitting, than distant or far ... we will have to know if the
 * scene is only a scene apart or if the scene is in combination with any of the first two options"). Each question says
 * what its answer changes, so the buyer knows why it is asked. The picture check's answer is picked already.
 * Fields: subject (person / object / scene), objectmotion (float / lean), distance (close / far); kept on the page only:
 * kind (the first answer). Standing or sitting is not asked (owner 2026-10-26: "lets just elimiate the swinging legs
 * stuff than it is a silly quesion to ask"; it changed nothing else).
 */
export function FoundStep({ depth, busy, subject, kind: kindSaid, objectmotion, distance, onChange, tap, tapping, onTapping, onClearTap, draft, onUndo, onDone }: {
  depth: DepthPreview | null; busy: boolean; subject: string; kind: string; objectmotion: string; distance: string;
  /** The fields an answer sets: subject, kind, and objectmotion / distance where they apply. */
  onChange: (patch: Record<string, string>) => void;
  /** Their rough paint as sent (strokes, "x,y x,y|..."), whether the picture is taking strokes now, and the strokes
   * painted since (kept until Done, so the picture is checked once for all of them). */
  tap: string; tapping: boolean; onTapping: (on: boolean) => void; onClearTap: () => void;
  draft: string; onUndo: () => void; onDone: () => void;
}) {
  const { t } = useTranslation();
  if (busy && !depth) {
    return (
      <p className="flex items-center gap-2 rounded-2xl bg-white/[0.05] px-3.5 py-3 text-[14px] text-foreground" role="status" data-testid="qt-found-busy">
        <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />{t(`${P}.found.looking`)}
      </p>
    );
  }
  const kind = foundKind(kindSaid, subject);
  // What stands in front: the answer itself, or for a scene what they said is in front of it (null: just the scene).
  const front: FrontKind | null = kind === 'scene' ? frontOf(subject) : kind;
  const dist = distanceOf(distance, depth);
  const setFront = (f: FrontKind | 'none', k: FoundKind) => {
    if (f === 'none') return onChange({ kind: k, subject: 'scene' });
    return onChange({ kind: k, subject: f === 'alive' ? 'person' : 'object', distance: dist, ...(f === 'object' ? { objectmotion: objectmotion === 'float' ? 'float' : 'lean' } : {}) });
  };
  const pick = (k: FoundKind) => setFront(k === 'scene' ? (front ?? 'none') : k, k);
  const question = (label: string, hint: string, opts: [string, string][], value: string, set: (v: string) => void, testid: string) => (
    <div className="mt-3 rounded-2xl bg-white/[0.04] p-3" data-testid={testid}>
      <p className="text-[14px] font-semibold leading-snug text-foreground">{label}</p>
      <div className="mt-2 flex gap-1.5" role="radiogroup" aria-label={label}>
        {opts.map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => set(v)} data-testid={`${testid}-${v}`}
            className={cn('min-h-[42px] flex-1 rounded-full px-2.5 text-[13px] font-semibold leading-tight transition-colors', value === v ? 'bg-white text-[#10131a]' : 'bg-white/[0.07] text-foreground hover:bg-white/[0.12]')}>
            {l}
          </button>
        ))}
      </div>
      <p className="mt-1.5 text-[12px] leading-snug text-muted-foreground">{hint}</p>
    </div>
  );
  const F = `${P}.found`;
  const sky = Boolean(depth?.sky?.found);
  const them = front === 'alive' ? 'them' : 'it';
  const unsure = depth?.paint?.unsure?.length ?? 0;
  return (
    <div data-testid="qt-found">
      <p className="flex items-center gap-2 text-[15px] font-semibold text-foreground"><ScanSearch className="h-4 w-4 text-primary" aria-hidden />{t(`${F}.title`)}</p>
      <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{t(`${F}.why`)}</p>
      <div className="mt-2.5 grid gap-2" role="radiogroup" aria-label={t(`${F}.title`)}>
        {FOUND_KINDS.map((k) => {
          const Icon = KIND_ICONS[k];
          const on = kind === k;
          return (
            <button key={k} type="button" role="radio" aria-checked={on} onClick={() => pick(k)} data-testid={`qt-found-${k}`}
              className={cn('flex min-h-[58px] w-full items-center gap-3 rounded-2xl px-3 text-left transition-colors', on ? 'bg-primary/15 ring-2 ring-primary' : 'bg-white/[0.05] hover:bg-white/[0.08]')}>
              <Icon className={cn('h-5 w-5 shrink-0', on ? 'text-primary' : 'text-foreground/70')} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-[14.5px] font-bold leading-tight text-foreground">{t(`${F}.kinds2.${k}`)}</span>
                <span className="block text-[12px] leading-snug text-muted-foreground">{t(`${F}.kinds2.${k}Hint`)}</span>
              </span>
              {on && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
            </button>
          );
        })}
      </div>
      {depth?.suggest && <p className="mt-2 text-[12.5px] leading-snug text-muted-foreground">{t(`${F}.fix`)}</p>}
      {kind === 'scene' && question(t(`${F}.front.label`), t(`${F}.front.hint`),
        [['none', t(`${F}.front.none`)], ['alive', t(`${F}.front.alive`)], ['object', t(`${F}.front.object`)]],
        front ?? 'none', (v) => setFront(v as FrontKind | 'none', 'scene'), 'qt-found-front')}
      {front === 'object' && question(t(`${F}.motion.label`), t(`${F}.motion.hint`),
        [['lean', t(`${F}.motion.still`)], ['float', t(`${F}.motion.moves`)]],
        objectmotion === 'float' ? 'float' : 'lean', (v) => onChange({ objectmotion: v }), 'qt-found-motion')}
      {front && question(t(`${F}.distance.label`), t(`${F}.distance.hint`),
        [['close', t(`${F}.close`)], ['far', t(`${F}.far`)]],
        dist, (v) => onChange({ distance: v }), 'qt-found-distance')}
      {/* What to cut out (owner 2026-10-26: "is it enough for the user to be able to give a quick reference paint to help
          zero in on the area and only asked to paint more exact when it either makes a mistake or truly cannot determine
          where it should be cropping?"): what we found is outlined; a rough stroke over each person or thing (two people,
          a person and a dog...) shows us them, and only a stroke we cannot settle asks for a more exact one. */}
      {front && (
        <div className="mt-3 rounded-2xl bg-white/[0.05] p-3" data-testid="qt-found-tap">
          <p className="flex items-center gap-2 text-[14px] font-semibold text-foreground"><Brush className="h-4 w-4 shrink-0 text-primary" aria-hidden />{t(`${F}.paint.title`)}</p>
          {tapping ? (
            <>
              <p className="mt-1 text-[12.5px] leading-snug text-muted-foreground" role="status">{t(`${F}.paint.now`)}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button type="button" onClick={onDone} disabled={!draft} data-testid="qt-found-paint-done"
                  className="flex min-h-[40px] items-center gap-2 rounded-full bg-primary px-4 text-[13px] font-bold text-primary-foreground disabled:opacity-50">
                  <Check className="h-4 w-4" aria-hidden />{t(`${F}.paint.done`)}
                </button>
                {draft && <button type="button" onClick={onUndo} className="min-h-[40px] rounded-full bg-white/[0.08] px-3 text-[12.5px] font-semibold text-foreground">{t(`${F}.paint.undo`)}</button>}
                <button type="button" onClick={() => onTapping(false)} className="min-h-[40px] rounded-full bg-white/[0.06] px-3 text-[12.5px] font-semibold text-foreground/80">{t(`${F}.paint.cancel`)}</button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-1 text-[12.5px] leading-snug text-muted-foreground" data-testid="qt-found-where">
                {tap
                  ? busy ? t(`${F}.looking`) : unsure > 0 ? t(`${F}.paint.unsure`, { count: unsure }) : depth?.tapped ? t(`${F}.paint.painted`) : t(`${F}.paint.missed`)
                  : t(depth?.depth ? `${F}.paint.found_${them}` : `${F}.paint.notFound_${them}`)}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => onTapping(true)} data-testid={tap ? 'qt-found-tap-again' : 'qt-found-tap-start'}
                  className="flex min-h-[40px] items-center gap-2 rounded-full bg-white/[0.1] px-3.5 text-[13px] font-semibold text-foreground hover:bg-white/[0.15]">
                  {busy && tap ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Brush className="h-4 w-4" aria-hidden />}
                  {t(tap ? `${F}.paint.again` : `${F}.paint.start_${them}`)}
                </button>
                {tap && (
                  <button type="button" onClick={onClearTap} className="min-h-[40px] rounded-full bg-white/[0.06] px-3 text-[12.5px] font-semibold text-foreground/80">{t(`${F}.paint.clear`)}</button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {sky && (
        <p className="mt-3 flex items-start gap-2 rounded-2xl bg-accent/10 px-3 py-2.5 text-[13.5px] leading-snug text-foreground" data-testid="qt-found-sky">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />{t(`${F}.sky`)}
        </p>
      )}
    </div>
  );
}

export type BehindMode = 'keep' | 'sky' | 'background';

/**
 * STEP: CROP, AND WHAT GOES IN PLACE OF WHAT IS CUT OUT (owner 2026-10-23: "a full crop to select a backdrop, ... a
 * partial crop like a sky above a city or ... no crop and we just give life to their currrent image"; 2026-10-26: "the
 * cropping option should also be comined with the backdrop option so they understand the reason for cropping and what
 * will go in place of the cropped out area"): three tiles of the buyer's own picture, and under the one picked, the new
 * sky or the new background to pick right there.
 */
export function BehindStep({ mode, onMode, photo, skyFound, canBackdrop, checking = false, checkFailed = false, onRetry, skyClip, onSkyClip, skyMove, onSkyMove, stars, onStars, backdrop, paint, view = 'select', onView }: {
  mode: BehindMode; onMode: (m: BehindMode) => void; photo: string | null; skyFound: boolean; canBackdrop: boolean;
  /** The picture check has not answered yet, or could not run: the choices it decides wait for it instead of saying "not found". */
  checking?: boolean; checkFailed?: boolean; onRetry?: () => void;
  skyClip: string; onSkyClip: (id: string) => void; skyMove: boolean; onSkyMove: (on: boolean) => void; stars: boolean; onStars: (on: boolean) => void;
  /** The background picker (the existing backdrop library), shown under the whole background. */
  backdrop: ReactNode;
  /** The brush over the picture: what to keep, or the part to replace (CropPaint). */
  paint: CropPaintProps;
  /** The picture shows what is selected (still, tinted) or the result (moving); the buyer switches. */
  view?: 'select' | 'result'; onView?: (v: 'select' | 'result') => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  // Every choice can be picked once the picture is checked (owner 2026-10-27: "it would only allow me to choose full
  // crop"): when we found nothing to cut, the buyer paints it, so a tile is never greyed for what we missed.
  const tiles = [['keep', ImageIcon, true], ['sky', Cloud, !checking], ['background', Mountain, !checking]] as [BehindMode, LucideIcon, boolean][];
  const skyItems: PickItem[] = useMemo(() => {
    const other = lang.startsWith('es') ? 'en' : 'es';
    const order = new Map(LIBRARY.backdropGroups.map((g, i) => [g.id, SKY_GROUPS.includes(g.id) ? i - 100 : i]));
    return REPLACE_IDS.map((id) => { const b = BACKDROPS[id]; return { id, label: lang.startsWith('es') ? b.es : b.en, group: b.group, thumb: b.thumb, preview: b.preview, words: `${b[other]} ${b.kind}` }; })
      .sort((a, b) => (order.get(a.group) ?? 99) - (order.get(b.group) ?? 99));
  }, [lang]);
  const groups = [...LIBRARY.backdropGroups.filter((g) => SKY_GROUPS.includes(g.id)), ...LIBRARY.backdropGroups.filter((g) => !SKY_GROUPS.includes(g.id))]
    .map((g) => ({ id: g.id, label: lang.startsWith('es') ? g.es : g.en }));
  // What the brush marks under each choice: the part to replace, or what to keep in front of a new background.
  const ready = mode === 'sky' ? skyFound : mode === 'background' ? canBackdrop : true;
  const picker = mode === 'keep' ? null : (
    <div className="mt-3" data-testid="qt-behind-pick">
      <CropPaint {...paint} which={mode === 'sky' ? 'replace' : 'keep'} found={ready} />
      {/* See it moving, or back to what is selected (owner 2026-10-27: hard to see the borders on the moving, glowing picture). */}
      {ready && onView && !paint.painting && (
        <div className="mt-2 grid grid-cols-2 gap-1 rounded-2xl bg-black/35 p-1" role="tablist" data-testid="qt-crop-view">
          {(['select', 'result'] as const).map((v) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => onView(v)} data-testid={`qt-crop-view-${v}`}
              className={cn('min-h-[40px] rounded-xl text-[13px] font-semibold', view === v ? 'bg-white/[0.12] text-foreground' : 'text-muted-foreground hover:text-foreground')}>
              {t(`${P}.brush.view.${v}`)}
            </button>
          ))}
        </div>
      )}
      {ready && <p className="mb-2 mt-4 text-[14px] font-semibold text-foreground">{t(`${P}.behind.${mode === 'sky' ? 'pickSky' : 'pickBackground'}`)}</p>}
      {ready && mode === 'sky' && (
        <LibraryPicker
          items={skyItems}
          groups={groups}
          picked={skyClip ? [skyClip] : []}
          max={1}
          single
          onToggle={(id) => onSkyClip(id === skyClip ? '' : id)}
          onNone={() => onSkyClip('')}
          noneLabel={t('qt.tools.canvas.fields.library.none')}
          searchLabel={t(`${P}.behind.skySearch`)}
          noMatch={t('qt.tools.canvas.fields.backdrop.noMatch')}
          fullLabel=""
          allLabel={t('qt.tools.canvas.fields.library.all')}
          clearLabel={t('qt.tools.canvas.fields.library.clearSearch')}
          offLabel={t('qt.tools.canvas.fields.library.tapOff')}
          base={{ photo }}
          backdrop
          testid="qt-library-sky"
        />
      )}
      {ready && mode === 'background' && backdrop}
    </div>
  );
  return (
    <div data-testid="qt-behind">
      <div className="grid gap-2" role="radiogroup" aria-label={t(`${P}.behind.title`)}>
        {tiles.map(([id, Icon, can]) => {
          const on = mode === id && can;
          return (
            <button key={id} type="button" role="radio" aria-checked={on} aria-disabled={!can} onClick={() => { if (can) onMode(id); }} data-testid={`qt-behind-${id}`}
              className={cn('group flex overflow-hidden rounded-2xl text-left ring-2 transition-colors', on ? 'ring-primary' : 'ring-transparent hover:ring-white/20', !can && 'cursor-not-allowed opacity-55 hover:ring-transparent')}>
              <span className="relative block w-24 shrink-0 self-stretch bg-black">
                {photo && <img src={photo} alt="" aria-hidden className={cn('absolute inset-0 h-full w-full object-cover', id !== 'keep' && 'opacity-60')} />}
                <span aria-hidden className="absolute bottom-1.5 left-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white"><Icon className="h-4 w-4" /></span>
                {on && <span aria-hidden className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>}
              </span>
              <span className={cn('flex min-h-[76px] flex-1 flex-col justify-center px-3 py-2.5', on ? 'bg-primary/15' : 'bg-white/[0.05]')}>
                <span className="block text-[14.5px] font-bold leading-tight text-foreground">{t(`${P}.behind.${id}`)}</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-[12px] leading-snug text-muted-foreground">
                  {!can && checking && <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />}
                  {t(`${P}.behind.${can ? `${id}Hint` : checking ? 'checking' : checkFailed ? 'checkFailed' : `${id}No`}`)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {checkFailed && !checking && onRetry && (
        <button type="button" onClick={onRetry} data-testid="qt-behind-retry"
          className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl bg-white/[0.08] px-3 text-[13.5px] font-semibold text-foreground hover:bg-white/[0.12]">
          <RotateCw className="h-4 w-4" aria-hidden />{t(`${P}.behind.retry`)}
        </button>
      )}
      {mode === 'keep' && skyFound && (
        <div className="mt-3 grid gap-2">
          <Switch on={skyMove} onChange={onSkyMove} icon={Wind} label={t(`${P}.behind.skyMove`)} testid="qt-sky-move" />
          {skyMove && <Switch on={stars} onChange={onStars} icon={Sparkles} label={t(`${P}.behind.stars`)} testid="qt-stars" />}
        </div>
      )}
      {picker}
    </div>
  );
}

function Switch({ on, onChange, icon: Icon, label, testid }: { on: boolean; onChange: (on: boolean) => void; icon: LucideIcon; label: string; testid: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} data-testid={testid}
      className="flex min-h-[50px] items-center gap-3 rounded-2xl bg-white/[0.05] px-3.5 text-left">
      <Icon className={cn('h-5 w-5 shrink-0', on ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
      <span className="flex-1 text-[14px] font-semibold text-foreground">{label}</span>
      <span aria-hidden className={cn('relative h-6 w-10 shrink-0 rounded-full transition-colors', on ? 'bg-primary' : 'bg-white/15')}>
        <i className={cn('absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform', on && 'translate-x-4')} />
      </span>
    </button>
  );
}

export interface CropPaintProps {
  /** The brush is out: what it marks ('keep' or 'replace'), and whether it rubs out. */
  painting: 'keep' | 'replace' | null; erase: boolean;
  /** What is marked already for each (the strokes as sent), the strokes painted since the brush came out, the check busy. */
  saved: { keep: string; replace: string }; draft: string; busy: boolean;
  onStart: (which: 'keep' | 'replace') => void; onErase: (on: boolean) => void; onUndo: () => void; onDone: () => void; onCancel: () => void;
  onClear: (which: 'keep' | 'replace') => void;
  /** As many strokes as one selection takes: the next one is not taken (Done, then paint again). */
  full?: boolean;
  /** The brush's size (CUT_SIZES). */
  size?: CutSize; onSize?: (s: CutSize) => void;
}

/**
 * THE BRUSH ON THE CROP STEP (owner 2026-10-27: "it had me paint the area i wanted cut out, which i did and it still didnt
 * really lock in to the area and actually cut too much, so maybe a eraser is a good addition"; "it actually cropped out
 * the entire image and left the area i painted that was suppose to get cropped.... so it did the function backwards"):
 * each choice says plainly what the paint means. A new sky or scene: paint the PART TO REPLACE. A new background: paint
 * WHAT TO KEEP. Rough strokes are outlined for them (EdgeSAM), the eraser rubs out what it should not take, undo takes
 * the last stroke back, and the result is tinted on the picture.
 */
function CropPaint({ which, found, painting, erase, saved, draft, busy, onStart, onErase, onUndo, onDone, onCancel, onClear, full = false, size = 'm', onSize }: CropPaintProps & { which: 'keep' | 'replace'; found: boolean }) {
  const { t } = useTranslation();
  const B = `${P}.brush`;
  const now = painting === which;
  const has = Boolean(saved[which]);
  const pill = 'flex min-h-[40px] items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold';
  // The 'erase' setting that brings the picture back: the keep brush paints what stays, the replace brush what goes.
  const addErase = which === 'replace';
  const adds = strokeAdds(which, erase);
  return (
    <div className="rounded-2xl bg-white/[0.05] p-3" data-testid={`qt-crop-paint-${which}`}>
      <p className="flex items-center gap-2 text-[14.5px] font-bold text-foreground"><Brush className="h-4 w-4 shrink-0 text-primary" aria-hidden />{t(`${B}.${which}.title`)}</p>
      <p className="mt-1 text-[12.5px] leading-snug text-muted-foreground" role="status" data-testid={`qt-crop-paint-${which}-say`}>
        {now ? (full ? t(`${B}.full`) : t(`${B}.${adds ? 'nowAdd' : 'nowCut'}`)) : busy ? t(`${B}.looking`) : t(`${B}.${which}.${found ? (has ? 'painted' : 'found') : 'none'}`)}
      </p>
      {now ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {/* Two tools by what they do to the picture (owner 2026-10-28): Bring back shows it again, Remove takes it away. */}
          <button type="button" aria-pressed={adds} onClick={() => onErase(addErase)} data-testid="qt-crop-add" className={cn(pill, adds ? 'bg-white text-[#10131a]' : 'bg-white/[0.08] text-foreground')}>
            <Brush className="h-4 w-4" aria-hidden />{t(`${B}.add`)}
          </button>
          <button type="button" aria-pressed={!adds} onClick={() => onErase(!addErase)} data-testid="qt-crop-cut" className={cn(pill, !adds ? 'bg-white text-[#10131a]' : 'bg-white/[0.08] text-foreground')}>
            <Eraser className="h-4 w-4" aria-hidden />{t(`${B}.cut`)}
          </button>
          {onSize && <CutSizes size={size} onSize={onSize} />}
          <button type="button" onClick={onUndo} disabled={!draft} data-testid="qt-crop-undo" className={cn(pill, 'bg-white/[0.08] text-foreground disabled:opacity-40')}>
            <Undo2 className="h-4 w-4" aria-hidden />{t(`${B}.undo`)}
          </button>
          <button type="button" onClick={onCancel} className={cn(pill, 'bg-white/[0.05] text-foreground/80')}>{t(`${B}.cancel`)}</button>
          <button type="button" onClick={onDone} data-testid="qt-crop-done" className={cn(pill, 'ml-auto bg-primary px-4 font-bold text-primary-foreground')}>
            <Check className="h-4 w-4" aria-hidden />{t(`${B}.done`)}
          </button>
        </div>
      ) : (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <button type="button" onClick={() => onStart(which)} disabled={painting !== null} data-testid={`qt-crop-start-${which}`}
            className={cn(pill, found ? 'bg-white/[0.1] text-foreground hover:bg-white/[0.15]' : 'bg-primary px-4 font-bold text-primary-foreground')}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Brush className="h-4 w-4" aria-hidden />}
            {t(`${B}.${found ? 'fix' : 'start'}`)}
          </button>
          {has && <button type="button" onClick={() => onClear(which)} className={cn(pill, 'bg-white/[0.06] text-foreground/80')} data-testid={`qt-crop-clear-${which}`}>{t(`${B}.clear`)}</button>}
        </div>
      )}
    </div>
  );
}

/** The brush's three sizes as dots (owner 2026-10-28: "difficult to get to smaller areas"). */
export function CutSizes({ size, onSize, dark = false }: { size: CutSize; onSize: (s: CutSize) => void; dark?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={cn('flex items-center gap-0.5 rounded-full p-0.5', dark ? 'bg-white/[0.1]' : 'bg-white/[0.06]')} role="radiogroup" aria-label={t(`${P}.brush.size`)} data-testid="qt-crop-sizes">
      {(['s', 'm', 'l'] as CutSize[]).map((k) => (
        <button key={k} type="button" role="radio" aria-checked={size === k} aria-label={t(`${P}.brush.sizes.${k}`)} title={t(`${P}.brush.sizes.${k}`)} onClick={() => onSize(k)} data-testid={`qt-crop-size-${k}`}
          className={cn('flex h-9 w-9 items-center justify-center rounded-full', size === k ? 'bg-white' : 'hover:bg-white/[0.08]')}>
          <span aria-hidden className={cn('rounded-full', size === k ? 'bg-[#10131a]' : 'bg-white/80')} style={{ width: { s: 5, m: 10, l: 16 }[k], height: { s: 5, m: 10, l: 16 }[k] }} />
        </button>
      ))}
    </div>
  );
}

/** Strokes as sent, "x,y x,y ...|..." in 0..1 of the picture: [[[x, y], ...], ...]; one rubbed out ('-') carries `erase`. */
export function decodeStrokes(text: string): ([number, number][] & { erase?: boolean; r?: number })[] {
  return String(text || '').split('|').map((part) => {
    const toks = part.trim().replace(/^-/, '').split(/\s+/);
    const st: [number, number][] & { erase?: boolean; r?: number } = toks.filter((p) => !p.startsWith('@')).map((p) => p.split(',').map(Number) as [number, number])
      .filter((p) => p.length === 2 && p.every((v) => Number.isFinite(v) && v >= 0 && v <= 1));
    if (part.trim().startsWith('-')) st.erase = true;
    // '@r': the brush's width it was made with (strokes.py size).
    const r = Number((toks.find((p) => p.startsWith('@')) || '').slice(1));
    if (Number.isFinite(r) && r > 0) st.r = r;
    return st;
  }).filter((st) => st.length);
}
/** One stroke as sent: its points 0..1, three decimals; `r` the crop brush's width ('@r', strokes.py size). */
export function encodeStroke(pts: [number, number][], r?: number): string {
  return `${r ? `@${r} ` : ''}${pts.map(([x, y]) => `${x.toFixed(3)},${y.toFixed(3)}`).join(' ')}`;
}

/**
 * The rough paint over the big picture (step: what is in it; owner 2026-10-26): while painting, each drag is one stroke
 * over a person or thing (points in 0..1 of the original picture, as the server reads them); the strokes show as a
 * broad bright line, and what the check outlined is tinted over the picture.
 */
export function TapLayer({ map, tap, draft, tapping, mask, onStroke, erase = false, tint = 'primary' }: {
  map: { toPicture: (u: number, v: number) => [number, number]; toFrame: (x: number, y: number) => [number, number] };
  tap: string; draft: string; tapping: boolean; mask: string | null; onStroke: (stroke: string) => void;
  /** The eraser is out: each stroke is sent rubbed out ('-'), drawn red. */
  erase?: boolean;
  /** The part to replace is tinted sky blue, what to keep gold. */
  tint?: 'primary' | 'sky';
}) {
  const [live, setLive] = useState<[number, number][]>([]);
  const drawing = useRef(false);
  const at = (e: ReactPointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return map.toPicture((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
  };
  const shown = tapping ? [...decodeStrokes(draft), ...(live.length ? [Object.assign(live.slice(), erase ? { erase: true } : {})] : [])] : decodeStrokes(tap);
  const line = (st: [number, number][]) => st.map((p) => map.toFrame(p[0], p[1])).map(([x, y]) => `${(x * 100).toFixed(2)},${(y * 100).toFixed(2)}`).join(' ');
  return (
    <div
      aria-hidden
      data-testid="qt-tap-layer"
      className={cn('absolute inset-0 touch-none rounded-2xl', tapping ? 'cursor-crosshair' : 'pointer-events-none')}
      onPointerDown={(e) => {
        if (!tapping) return;
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        drawing.current = true;
        const p = at(e);
        setLive(p[0] >= 0 && p[0] <= 1 && p[1] >= 0 && p[1] <= 1 ? [p] : []);
      }}
      onPointerMove={(e) => {
        if (!tapping || !drawing.current) return;
        const p = at(e);
        if (p[0] < 0 || p[0] > 1 || p[1] < 0 || p[1] > 1) return;
        setLive((pts) => {
          const last = pts[pts.length - 1];
          // A point every 1% of the picture, at most 300 a stroke (the field stays small; nothing painted is dropped).
          return !last || Math.hypot(p[0] - last[0], p[1] - last[1]) > 0.01 ? (pts.length < 300 ? [...pts, p] : pts) : pts;
        });
      }}
      onPointerUp={() => {
        if (!drawing.current) return;
        drawing.current = false;
        if (live.length) onStroke(`${erase ? '-' : ''}${encodeStroke(live)}`);
        setLive([]);
      }}
      onPointerCancel={() => { drawing.current = false; setLive([]); }}
    >
      {mask && (
        <span className={cn('absolute inset-0 rounded-2xl', tint === 'sky' ? 'bg-sky-400/55' : 'bg-primary/55', tapping && 'opacity-60')} style={{ WebkitMaskImage: `url(${mask})`, maskImage: `url(${mask})`, WebkitMaskSize: '100% 100%', maskSize: '100% 100%' }} />
      )}
      {tapping && <span className={cn('absolute inset-0 rounded-2xl ring-2 ring-inset', erase ? 'ring-red-400' : tint === 'sky' ? 'ring-sky-400' : 'ring-primary')} />}
      {shown.length > 0 && (
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {shown.map((st, i) => {
            // The paint in its colour; a rubbed-out stroke red (the live stroke with the eraser out, too).
            const c = st.erase ? 'rgb(248,113,113)' : tint === 'sky' ? 'rgb(56,189,248)' : 'hsl(var(--primary))';
            return st.length === 1
              ? <circle key={i} cx={map.toFrame(st[0][0], st[0][1])[0] * 100} cy={map.toFrame(st[0][0], st[0][1])[1] * 100} r={1.6} fill={c} fillOpacity={tapping ? 0.75 : 0.45} />
              : <polyline key={i} points={line(st)} fill="none" stroke={c} strokeOpacity={tapping ? 0.75 : 0.45} strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 14 }} />;
          })}
        </svg>
      )}
    </div>
  );
}

/** How wide the crop brush is, each side of its line: a share of the picture's short side (strokes.py BRUSH_R). */
export const CUT_BRUSH_R = 0.035;
/** A stroke whose points all stay this close to where it started is a tap (strokes.py TAP): the whole thing under it. */
export const CUT_TAP = 0.012;

type CutMap = { toPicture: (u: number, v: number) => [number, number]; toFrame: (x: number, y: number) => [number, number] };
type CutStroke = [number, number][] & { erase?: boolean; r?: number };

/** Whether a stroke brings the picture back (else it removes it): the keep brush paints what stays, the replace brush
 *  what goes, and the other tool ('-') does the opposite. */
export function strokeAdds(which: 'keep' | 'replace', erase: boolean): boolean {
  return (which === 'keep') !== erase;
}

/** What stays, drawn white on `g` (w x h): the check's cut, or all or nothing without one, the strokes since laid over it. */
function layCut(g: CanvasRenderingContext2D, w: number, h: number, mask: HTMLImageElement | null, none: 'empty' | 'full', strokes: CutStroke[], which: 'keep' | 'replace', map: CutMap) {
  g.clearRect(0, 0, w, h);
  if (mask) g.drawImage(mask, 0, 0, w, h);
  else if (none === 'full') { g.fillStyle = '#fff'; g.fillRect(0, 0, w, h); }
  g.strokeStyle = '#fff';
  g.fillStyle = '#fff';
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const st of strokes) {
    const r = (st.r || CUT_BRUSH_R) * Math.min(w, h);
    g.lineWidth = 2 * r;
    g.globalCompositeOperation = strokeAdds(which, Boolean(st.erase)) ? 'source-over' : 'destination-out';
    const pts = st.map((p) => map.toFrame(p[0], p[1])).map(([x, y]) => [x * w, y * h] as const);
    g.beginPath();
    if (pts.length === 1) {
      g.arc(pts[0][0], pts[0][1], r, 0, Math.PI * 2);
      g.fill();
    } else {
      pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
      g.stroke();
    }
  }
}

/**
 * THE CUT AS THE BUYER LEFT IT (owner 2026-11-01: "when you than add the backdrop what you cropped appears different ...
 * almost like it just decided to redo the crop itself"): the strokes the check has not answered yet showed on the Crop
 * step's picture and nowhere else, so the backdrop stood behind the older cut. This draws them into that cut, as the
 * mask and its inverse the stage uses (white, their alpha), the same way CutLayer does. null when nothing is pending and
 * the mask is the last check's (`moved`: an earlier answer, after an Undo).
 */
export function useLiveCut(mask: string | null, pending: string, which: 'keep' | 'replace' | null, map: CutMap | null, moved = false): { mask: string; inv: string } | null {
  const [cut, setCut] = useState<{ mask: string; inv: string } | null>(null);
  useEffect(() => {
    if (!mask || (!pending && !moved) || !which || !map) {
      setCut(null);
      return undefined;
    }
    let off = false;
    const im = new Image();
    im.onload = () => {
      if (off) return;
      const w = im.naturalWidth;
      const h = im.naturalHeight;
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const inv = document.createElement('canvas');
      inv.width = w;
      inv.height = h;
      const g = c.getContext('2d');
      const gi = inv.getContext('2d');
      if (!g || !gi || !w || !h) return;
      layCut(g, w, h, im, 'empty', decodeStrokes(pending), which, map);
      gi.fillStyle = '#fff';
      gi.fillRect(0, 0, w, h);
      gi.globalCompositeOperation = 'destination-out';
      gi.drawImage(c, 0, 0);
      setCut({ mask: c.toDataURL('image/png'), inv: inv.toDataURL('image/png') });
    };
    im.src = mask;
    return () => { off = true; };
  }, [mask, pending, which, map, moved]);
  return cut;
}

/**
 * THE CUT, EDITED DIRECTLY (owner 2026-10-28: "couldnt the painting just remove the erased part and bring back what was
 * underneath it? and the eraser do the opposite?"; "i dont like the fact that we need to paint a color over them to than
 * wait"). What stays is the picture itself, drawn through the cut the check made; each stroke not yet in that cut is
 * laid on at once: Bring back shows the picture under the brush, Remove takes it away. A tap is sent as one point: the
 * check then brings back (or removes) the whole person or thing under it ("how much needs to be done manually before the
 * tool can understand that is a person" -> one tap); meanwhile the spot under the finger shows.
 */
export function CutLayer({ map, picture, original = null, mask, none, which, pending, active, adds, onStroke, brushR = CUT_BRUSH_R, blocked = false }: {
  map: CutMap;
  /** The frame's still, as shown under it. */
  picture: string | null;
  /** The buyer's own picture at its full size, drawn where it sits in the frame: sharp when zoomed in close. */
  original?: string | null;
  /** What stays, as the check cut it (white, its alpha what stays), in the frame; null when it has not cut yet. */
  mask: string | null;
  /** With no cut: nothing stays (removing the background) or everything does (removing a part). */
  none: 'empty' | 'full';
  which: 'keep' | 'replace';
  /** Strokes made since that cut ("x,y ...|-x,y ..."), laid on over it. */
  pending: string;
  /** The brush is out; `adds`: it brings the picture back (else it removes). */
  active: boolean; adds: boolean;
  onStroke: (stroke: string) => void;
  /** The brush's width, each side of its line: a share of the frame's short side (CUT_SIZES). */
  brushR?: number;
  /** Two fingers are zooming (ZoomBox): the stroke begun is let go. */
  blocked?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<[number, number]>([0, 0]);
  const [live, setLiveState] = useState<[number, number][]>([]);
  // The stroke being made, read at once on lift (a quick tap lifts before the state has caught up).
  const liveRef = useRef<[number, number][]>([]);
  const setLive = (next: [number, number][] | ((pts: [number, number][]) => [number, number][])) => {
    liveRef.current = typeof next === 'function' ? next(liveRef.current) : next;
    setLiveState(liveRef.current);
  };
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const [imgs, setImgs] = useState<{ pic: HTMLImageElement | null; mask: HTMLImageElement | null; own: HTMLImageElement | null }>({ pic: null, mask: null, own: null });
  const zoom = useContext(ZoomScale);
  const drawing = useRef(false);
  const erase = adds !== strokeAdds(which, false);
  useEffect(() => {
    if (!blocked) return;
    drawing.current = false;
    setLive([]);
  }, [blocked]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    setSize([el.clientWidth, el.clientHeight]);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setSize([el.clientWidth, el.clientHeight]));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    let off = false;
    const load = (src: string | null) => new Promise<HTMLImageElement | null>((res) => {
      if (!src) { res(null); return; }
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => res(null);
      im.src = src;
    });
    void Promise.all([load(picture), load(mask), load(original)]).then(([pic, m, own]) => { if (!off) setImgs({ pic, mask: m, own }); });
    return () => { off = true; };
  }, [picture, mask, original]);
  // The brush's width in the frame's pixels: a share of its short side, as the server lays it on the frame's still.
  const brushPx = (w: number, h: number, r = brushR) => r * Math.min(w, h);
  useEffect(() => {
    const c = canvas.current;
    const [w, h] = size;
    if (!c || !w || !h) return;
    // As many pixels as the zoom shows (at most 4096 a side): the edges stay sharp up close.
    const k = Math.max(1, Math.min((window.devicePixelRatio || 1) * zoom, 4096 / Math.max(w, h)));
    c.width = Math.round(w * k);
    c.height = Math.round(h * k);
    const g = c.getContext('2d');
    if (!g) return;
    g.setTransform(k, 0, 0, k, 0, 0);
    g.imageSmoothingQuality = 'high';
    layCut(g, w, h, imgs.mask, none, [...decodeStrokes(pending), ...(live.length ? [Object.assign(live.slice(), { erase, r: brushR })] : [])], which, map);
    // The picture where it stays (cover-fit, as the <img> under it).
    g.globalCompositeOperation = 'source-in';
    const pic = imgs.pic;
    if (pic && pic.naturalWidth) {
      const f = Math.max(w / pic.naturalWidth, h / pic.naturalHeight);
      const dw = pic.naturalWidth * f;
      const dh = pic.naturalHeight * f;
      g.drawImage(pic, (w - dw) / 2, (h - dh) / 2, dw, dh);
    }
    // The buyer's own picture over its place in the frame, at its full size (the frame's still is half size).
    if (imgs.own && imgs.own.naturalWidth) {
      const [x0, y0] = map.toFrame(0, 0);
      const [x1, y1] = map.toFrame(1, 1);
      g.drawImage(imgs.own, x0 * w, y0 * h, (x1 - x0) * w, (y1 - y0) * h);
    }
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = 'source-over';
  }, [size, imgs, pending, live, which, erase, none, brushR, zoom]); // eslint-disable-line react-hooks/exhaustive-deps
  const at = (e: ReactPointerEvent<HTMLDivElement>) => {
    const b = e.currentTarget.getBoundingClientRect();
    return map.toPicture((e.clientX - b.left) / b.width, (e.clientY - b.top) / b.height);
  };
  const inside = (p: [number, number]) => p[0] >= 0 && p[0] <= 1 && p[1] >= 0 && p[1] <= 1;
  const r = size[0] ? brushPx(size[0], size[1]) : 0;
  return (
    <div
      ref={box}
      aria-hidden
      data-testid="qt-cut-layer"
      className={cn('absolute inset-0 touch-none', active ? 'cursor-none' : 'pointer-events-none')}
      onPointerDown={(e) => {
        if (!active || blocked) return;
        e.stopPropagation();
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the stroke is kept without it */ }
        drawing.current = true;
        const p = at(e);
        setLive(inside(p) ? [p] : []);
      }}
      onPointerMove={(e) => {
        if (!active) return;
        // In the layer's own pixels (it may be zoomed: ZoomBox scales it).
        const b = e.currentTarget.getBoundingClientRect();
        const k = e.currentTarget.clientWidth / (b.width || 1);
        setCursor([(e.clientX - b.left) * k, (e.clientY - b.top) * k]);
        if (!drawing.current) return;
        const p = at(e);
        if (!inside(p)) return;
        setLive((pts) => {
          const last = pts[pts.length - 1];
          // A point every 0.4% of the picture, at most 400 a stroke (simplified on lift; nothing painted is dropped).
          return !last || Math.hypot(p[0] - last[0], p[1] - last[1]) > 0.004 ? (pts.length < 400 ? [...pts, p] : pts) : pts;
        });
      }}
      onPointerUp={() => {
        if (!drawing.current) return;
        drawing.current = false;
        const pts = liveRef.current;
        if (pts.length && !blocked) {
          // A tap stays a tap (one point) however the finger wobbled: the whole thing under it.
          const tap = pts.every((p) => Math.abs(p[0] - pts[0][0]) <= CUT_TAP && Math.abs(p[1] - pts[0][1]) <= CUT_TAP);
          onStroke(`${erase ? '-' : ''}${encodeStroke(tap ? [pts[0]] : simplifyStroke(pts), tap ? undefined : brushR)}`);
        }
        setLive([]);
      }}
      onPointerLeave={() => setCursor(null)}
      onPointerCancel={() => { drawing.current = false; setLive([]); }}
    >
      <canvas ref={canvas} className="pointer-events-none absolute inset-0 h-full w-full" data-testid="qt-crop-kept" />
      {active && cursor && r > 0 && (
        <span className={cn('pointer-events-none absolute rounded-full border-2', adds ? 'border-white' : 'border-red-400')}
          style={{ left: cursor[0] - r, top: cursor[1] - r, width: 2 * r, height: 2 * r, boxShadow: '0 0 0 1px rgba(0,0,0,0.6)' }} />
      )}
    </div>
  );
}

/** The crop brush's sizes (owner 2026-10-28: "the brush ... is now currently only available in one size which makes it a
 *  little difficult to get to smaller areas"): each side of its line, a share of the frame's short side. */
export const CUT_SIZES = { s: 0.012, m: 0.025, l: 0.045 } as const;
export type CutSize = keyof typeof CUT_SIZES;

/** Fewer points on a stroke, its shape kept within `eps` (Douglas-Peucker): a long cleanup stays light to send. */
export function simplifyStroke(pts: [number, number][], eps = 0.0015): [number, number][] {
  if (pts.length < 3) return pts;
  const keep = new Array(pts.length).fill(false);
  keep[0] = keep[pts.length - 1] = true;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const [ax, ay] = pts[a];
    const [bx, by] = pts[b];
    const len = Math.hypot(bx - ax, by - ay) || 1e-9;
    let far = -1;
    let at = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs((bx - ax) * (ay - pts[i][1]) - (ax - pts[i][0]) * (by - ay)) / len;
      if (d > far) { far = d; at = i; }
    }
    if (far > eps && at > 0) {
      keep[at] = true;
      stack.push([a, at], [at, b]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

/**
 * ZOOM ON THE PICTURE WHILE CUTTING (owner 2026-10-28: "we need to be able to zoom in close whether its a phone or on pc
 * ... when we are cleaning up the lines from a sloppy crop we can get in close and make sure to be clean and accurate").
 * Phone: two fingers pinch to zoom and drag to move round. Computer: the mouse wheel zooms where it points; with the
 * hand on, a drag moves round. Buttons for both: zoom in, zoom out, fit, and the hand. One finger (or the mouse) still
 * paints when the hand is off. `onPinch` says when two fingers are down: the brush lets go of the stroke it began.
 */
/** How far ZoomBox has zoomed in: what it holds draws itself sharp at that size (CutLayer). */
export const ZoomScale = createContext(1);
/** How close the picture can be zoomed (owner 2026-10-28: "actually zoom in close as i want to.. no just somewhat"). */
export const ZOOM_MAX = 24;

export function ZoomBox({ children, hand, onHand, onPinch, labels }: {
  children: ReactNode;
  hand: boolean; onHand: (on: boolean) => void;
  onPinch: (on: boolean) => void;
  labels: { zoomIn: string; zoomOut: string; fit: string; hand: string };
}) {
  const box = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const pts = useRef(new Map<number, [number, number]>());
  const gesture = useRef<{ s: number; x: number; y: number; d: number; mx: number; my: number } | null>(null);
  const pan = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  // Held Space (a computer) moves round like the hand, for as long as it is held.
  const [space, setSpace] = useState(false);
  const moving = hand || space;
  useEffect(() => {
    const typing = (e: KeyboardEvent) => (e.target as HTMLElement | null)?.closest?.('input, textarea, [contenteditable="true"]');
    const down = (e: KeyboardEvent) => { if (e.code === 'Space' && !typing(e)) { e.preventDefault(); setSpace(true); } };
    const up = (e: KeyboardEvent) => { if (e.code === 'Space') setSpace(false); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);
  // Kept inside the frame: the picture always covers it.
  const fit = (s: number, x: number, y: number) => {
    const el = box.current;
    const w = el?.clientWidth || 1;
    const h = el?.clientHeight || 1;
    const k = Math.min(ZOOM_MAX, Math.max(1, s));
    return { s: k, x: Math.min(0, Math.max(w * (1 - k), x)), y: Math.min(0, Math.max(h * (1 - k), y)) };
  };
  // Zoomed about a point (px from the box's top-left).
  const zoomAt = (k: number, cx: number, cy: number) => {
    const v = viewRef.current;
    const s = Math.min(ZOOM_MAX, Math.max(1, v.s * k));
    setView(fit(s, cx - ((cx - v.x) * s) / v.s, cy - ((cy - v.y) * s) / v.s));
  };
  const local = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = box.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const [cx, cy] = local(e);
      zoomAt(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0025)), cx, cy);
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const startPinch = () => {
    const [a, b] = [...pts.current.values()];
    const v = viewRef.current;
    gesture.current = { ...v, d: Math.hypot(b[0] - a[0], b[1] - a[1]) || 1, mx: (a[0] + b[0]) / 2, my: (a[1] + b[1]) / 2 };
    onPinch(true);
  };
  const btn = 'flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/15 backdrop-blur disabled:opacity-40';
  const w = box.current?.clientWidth || 0;
  const h = box.current?.clientHeight || 0;
  return (
    <div
      ref={box}
      className={cn('absolute inset-0 touch-none overflow-hidden', moving && 'cursor-grab')}
      data-testid="qt-zoom-box"
      onPointerDownCapture={(e) => {
        // Its own buttons are buttons, whatever is on (the hand once took their clicks and could not be put down).
        if ((e.target as HTMLElement).closest('[data-zoom-tools]')) return;
        pts.current.set(e.pointerId, local(e));
        if (pts.current.size === 2) { startPinch(); e.stopPropagation(); return; }
        // The hand, held Space, or the mouse's middle button: a drag moves round.
        if ((moving || e.button === 1) && pts.current.size === 1) {
          const v = viewRef.current;
          pan.current = { x: v.x, y: v.y, px: local(e)[0], py: local(e)[1] };
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* panned without it */ }
          e.stopPropagation();
        }
      }}
      onPointerMoveCapture={(e) => {
        if (!pts.current.has(e.pointerId)) return;
        pts.current.set(e.pointerId, local(e));
        const g = gesture.current;
        if (g && pts.current.size >= 2) {
          const [a, b] = [...pts.current.values()];
          const d = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
          const mx = (a[0] + b[0]) / 2;
          const my = (a[1] + b[1]) / 2;
          const s = Math.min(ZOOM_MAX, Math.max(1, (g.s * d) / g.d));
          // The point under the fingers' middle stays under it, and moves with it.
          setView(fit(s, mx - ((g.mx - g.x) * s) / g.s, my - ((g.my - g.y) * s) / g.s));
          e.stopPropagation();
          return;
        }
        const p = pan.current;
        if (p) {
          const [cx, cy] = local(e);
          setView(fit(viewRef.current.s, p.x + cx - p.px, p.y + cy - p.py));
          e.stopPropagation();
        }
      }}
      onPointerUpCapture={(e) => {
        pts.current.delete(e.pointerId);
        if (gesture.current && pts.current.size < 2) {
          gesture.current = null;
          if (!pts.current.size) onPinch(false);
          e.stopPropagation();
        }
        if (!pts.current.size) pan.current = null;
      }}
      onPointerCancelCapture={(e) => {
        pts.current.delete(e.pointerId);
        if (pts.current.size < 2) gesture.current = null;
        if (!pts.current.size) { pan.current = null; onPinch(false); }
      }}
    >
      <div className="absolute inset-0 origin-top-left will-change-transform" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})` }}>
        <ZoomScale.Provider value={view.s}>{children}</ZoomScale.Provider>
      </div>
      <div className="absolute right-2 top-2 z-10 flex flex-col gap-1.5" data-testid="qt-zoom-tools" data-zoom-tools onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" aria-label={labels.zoomIn} title={labels.zoomIn} className={btn} disabled={view.s >= ZOOM_MAX} onClick={() => zoomAt(2, w / 2, h / 2)} data-testid="qt-zoom-in"><ZoomIn className="h-4 w-4" aria-hidden /></button>
        <button type="button" aria-label={labels.zoomOut} title={labels.zoomOut} className={btn} disabled={view.s <= 1} onClick={() => zoomAt(1 / 2, w / 2, h / 2)} data-testid="qt-zoom-out"><ZoomOut className="h-4 w-4" aria-hidden /></button>
        <button type="button" aria-label={labels.fit} title={labels.fit} className={btn} disabled={view.s <= 1} onClick={() => setView({ s: 1, x: 0, y: 0 })} data-testid="qt-zoom-fit"><Maximize2 className="h-4 w-4" aria-hidden /></button>
        <button type="button" aria-label={labels.hand} title={labels.hand} aria-pressed={hand} className={cn(btn, hand && 'bg-white text-[#10131a]')} onClick={() => onHand(!hand)} data-testid="qt-zoom-hand"><Hand className="h-4 w-4" aria-hidden /></button>
      </div>
      {view.s > 1 && <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/70 px-2 py-0.5 text-[11.5px] font-semibold text-white">{Math.round(view.s * 100)}%</span>}
    </div>
  );
}

export interface PaintState { kind: PaintKind | null; brush: BrushSize; erase: boolean }

type FarMotion = 'push' | 'smoke' | 'walk';
const FAR_MOTIONS: [FarMotion, LucideIcon][] = [['push', ZoomIn], ['smoke', Cigarette], ['walk', Footprints]];

/**
 * STEP: MAKE IT MOVE. Chips already on when they fit, and one button to paint something to move. A subject in the
 * distance gets three more, off until picked (owner 2026-10-26): the camera pushes in, smoke rising, walks across.
 */
export function MoveStep({ canFloat, floatOn, onFloat, canHair, hairOn, onHair, far, farOn, onFar, paint, onPaint, painting, onPainting, busy }: {
  canFloat: boolean; floatOn: boolean; onFloat: (on: boolean) => void; canHair: boolean; hairOn: boolean; onHair: (on: boolean) => void;
  far: Record<FarMotion, boolean>; farOn: Record<FarMotion, boolean>; onFar: (k: FarMotion, on: boolean) => void;
  paint: string; onPaint: (v: string) => void; painting: PaintState; onPainting: (p: PaintState) => void; busy: boolean;
}) {
  const { t } = useTranslation();
  const regions = useMemo(() => decodePaint(paint), [paint]);
  const setRegions = (next: PaintRegion[]) => onPaint(encodePaint(next));
  const current = painting.kind ? regions.find((g) => g.k === painting.kind) : null;
  const turn = () => {
    if (!painting.kind) return;
    const base = current?.d || PAINT_DIR[painting.kind];
    if (!base) return;
    const i = TURN.findIndex(([x, y]) => Math.sign(x) === Math.sign(Math.round(base[0])) && Math.sign(y) === Math.sign(Math.round(base[1])));
    const d = TURN[(i + 1) % TURN.length];
    setRegions(regions.map((g) => (g.k === painting.kind ? { ...g, d } : g)));
  };
  const flipPole = () => setRegions(regions.map((g) => (g.k === 'flag' ? { ...g, root: (g.root || 'left') === 'left' ? 'right' : 'left' } : g)));
  return (
    <div data-testid="qt-move">
      {(canFloat || canHair) && (
        <div className="flex flex-wrap gap-2">
          {canFloat && <Chip on={floatOn} onClick={() => onFloat(!floatOn)} icon={Sparkles} label={t(`${P}.move.float`)} testid="qt-move-float" />}
          {canHair && <Chip on={hairOn} onClick={() => onHair(!hairOn)} icon={Wind} label={t(`${P}.move.hair`)} testid="qt-move-hair" />}
        </div>
      )}
      {FAR_MOTIONS.some(([k]) => far[k]) && (
        <div className={cn('rounded-2xl bg-white/[0.04] p-3', (canFloat || canHair) && 'mt-3')} data-testid="qt-move-far">
          <p className="text-[14px] font-semibold leading-snug text-foreground">{t(`${P}.move.farTitle`)}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {FAR_MOTIONS.filter(([k]) => far[k]).map(([k, Icon]) => (
              <Chip key={k} on={farOn[k]} onClick={() => onFar(k, !farOn[k])} icon={Icon} label={t(`${P}.move.${k}`)} testid={`qt-move-${k}`} />
            ))}
          </div>
          <p className="mt-1.5 text-[12px] leading-snug text-muted-foreground">{t(`${P}.move.farHint`)}</p>
        </div>
      )}
      <div className="mt-4 rounded-2xl border border-dashed border-white/20 p-3">
        <p className="flex items-center gap-2 text-[14.5px] font-semibold text-foreground"><Brush className="h-4 w-4 text-primary" aria-hidden />{t(`${P}.move.paintTitle`)}</p>
        <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{t(painting.kind ? `${P}.move.paintNow` : `${P}.move.paintHint`)}</p>
        <div className="mt-2.5 grid grid-cols-2 gap-1.5">
          {[...MOVE_KINDS, ...regions.map((g) => g.k).filter((k) => !MOVE_KINDS.includes(k))].map((k) => {
            const Icon = PAINT_ICONS[k];
            const on = painting.kind === k;
            const has = regions.some((g) => g.k === k);
            const kind = MOVE_KINDS.includes(k);
            return (
              <button key={k} type="button" aria-pressed={on} data-testid={`qt-paint-${k}`}
                onClick={() => {
                  if (!has && regions.length >= 6) return;
                  onPainting({ ...painting, kind: on ? null : k, erase: false });
                }}
                className={cn('relative flex min-h-[64px] items-center gap-2.5 rounded-xl px-3 text-left transition-colors',
                  on ? 'bg-primary text-primary-foreground' : 'bg-white/[0.05] text-foreground hover:bg-white/[0.09]')}>
                <Icon className="h-5 w-5 shrink-0" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-bold leading-tight">{kind ? t(`${P}.kinds.${k}.name`) : t(`${P}.paint.${k}`)}</span>
                  {kind && <span className={cn('block text-[11.5px] leading-snug', on ? 'text-primary-foreground/80' : 'text-muted-foreground')}>{t(`${P}.kinds.${k}.eg`)}</span>}
                </span>
                {has && !on && <span aria-hidden className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full" style={{ background: `rgb(${PAINT_TINT[k]})` }} />}
              </button>
            );
          })}
        </div>
        {painting.kind && (
          <div className="mt-3 grid gap-2" data-testid="qt-paint-tools">
            <div className="flex flex-wrap items-center gap-1.5">
              {(Object.keys(BRUSHES) as BrushSize[]).map((b) => (
                <button key={b} type="button" aria-pressed={painting.brush === b && !painting.erase} onClick={() => onPainting({ ...painting, brush: b, erase: false })}
                  className={cn('flex min-h-[40px] items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold', painting.brush === b && !painting.erase ? 'bg-white text-[#10131a]' : 'bg-white/[0.07] text-foreground')}>
                  <span aria-hidden className="rounded-full bg-current" style={{ width: b === 's' ? 6 : b === 'm' ? 10 : 15, height: b === 's' ? 6 : b === 'm' ? 10 : 15 }} />
                  {t(`${P}.paint.brush.${b}`)}
                </button>
              ))}
              <button type="button" aria-pressed={painting.erase} onClick={() => onPainting({ ...painting, erase: !painting.erase })} data-testid="qt-paint-erase"
                className={cn('flex min-h-[40px] items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold', painting.erase ? 'bg-white text-[#10131a]' : 'bg-white/[0.07] text-foreground')}>
                <Eraser className="h-4 w-4" aria-hidden />{t(`${P}.paint.erase`)}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PAINT_DIR[painting.kind] && (
                <button type="button" onClick={turn} data-testid="qt-paint-turn" className="flex min-h-[40px] items-center gap-1.5 rounded-full bg-white/[0.07] px-3 text-[12.5px] font-semibold text-foreground">
                  <RotateCw className="h-4 w-4" aria-hidden />{t(`${P}.paint.turn`)}
                </button>
              )}
              {painting.kind === 'flag' && (
                <button type="button" onClick={flipPole} data-testid="qt-paint-pole" className="flex min-h-[40px] items-center gap-1.5 rounded-full bg-white/[0.07] px-3 text-[12.5px] font-semibold text-foreground">
                  <Flag className="h-4 w-4" aria-hidden />{t((current?.root || 'left') === 'left' ? `${P}.paint.poleLeft` : `${P}.paint.poleRight`)}
                </button>
              )}
              <button type="button" onClick={() => setRegions(regions.filter((g) => g.k !== painting.kind))} data-testid="qt-paint-clear" className="flex min-h-[40px] items-center gap-1.5 rounded-full bg-white/[0.07] px-3 text-[12.5px] font-semibold text-foreground">
                <Trash2 className="h-4 w-4" aria-hidden />{t(`${P}.paint.clear`)}
              </button>
              <button type="button" onClick={() => onPainting({ ...painting, kind: null, erase: false })} data-testid="qt-paint-done" className="ml-auto flex min-h-[40px] items-center gap-1.5 rounded-full bg-primary px-4 text-[13px] font-bold text-primary-foreground">
                <Check className="h-4 w-4" aria-hidden />{t(`${P}.paint.done`)}
              </button>
            </div>
          </div>
        )}
        {!painting.kind && regions.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {regions.map((g) => (
              <span key={g.k} className="inline-flex items-center rounded-full bg-white/[0.08]">
                <button type="button" onClick={() => onPainting({ ...painting, kind: g.k })} className="min-h-[34px] pl-3 pr-1.5 text-[12.5px] font-semibold text-foreground">{MOVE_KINDS.includes(g.k) ? t(`${P}.kinds.${g.k}.name`) : t(`${P}.paint.${g.k}`)}</button>
                <button type="button" onClick={() => setRegions(regions.filter((r) => r.k !== g.k))} aria-label={t(`${P}.paint.remove`, { name: MOVE_KINDS.includes(g.k) ? t(`${P}.kinds.${g.k}.name`) : t(`${P}.paint.${g.k}`) })} className="flex h-[34px] w-8 items-center justify-center text-foreground/70 hover:text-foreground">
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      {busy && (
        <p className="mt-3 flex items-center gap-2 text-[12.5px] text-muted-foreground" role="status"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{t(`${P}.making`)}</p>
      )}
    </div>
  );
}

function Chip({ on, onClick, icon: Icon, label, testid }: { on: boolean; onClick: () => void; icon: LucideIcon; label: string; testid: string }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} data-testid={testid}
      className={cn('inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-[14px] font-semibold transition-colors',
        on ? 'bg-accent text-[#10131a]' : 'bg-white/[0.07] text-foreground hover:bg-white/[0.12]')}>
      {on ? <Check className="h-4 w-4" aria-hidden /> : <Icon className="h-4 w-4" aria-hidden />}{label}
    </button>
  );
}

/**
 * The paint layer over the big picture while painting: every painted area tinted, and a finger or the mouse paints
 * (or rubs out) the area of the kind picked. Strokes are kept in the original picture's shares (toPicture).
 */
export function PaintLayer({ paint, onPaint, painting, map }: {
  paint: string; onPaint: (v: string) => void; painting: PaintState;
  map: { toPicture: (u: number, v: number) => [number, number]; toFrame: (x: number, y: number) => [number, number]; radiusInFrame: (r: number) => number };
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const regions = useMemo(() => decodePaint(paint), [paint]);
  const live = useRef<Stroke | null>(null);
  const [tick, setTick] = useState(0);
  const draw = () => {
    const c = canvasRef.current;
    if (!c) return;
    const w = c.clientWidth; const h = c.clientHeight;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
    const g = c.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const strokesOf = (k: PaintKind) => [...(regions.find((r) => r.k === k)?.strokes || []), ...(live.current && painting.kind === k ? [live.current] : [])];
    for (const k of PAINT_KINDS) {
      const strokes = strokesOf(k);
      if (!strokes.length) continue;
      // Each kind on its own layer (rubbing out removes only its own paint), then laid on tinted.
      const off = document.createElement('canvas');
      off.width = c.width; off.height = c.height;
      const o = off.getContext('2d');
      if (!o) continue;
      o.setTransform(dpr, 0, 0, dpr, 0, 0);
      o.lineCap = 'round'; o.lineJoin = 'round';
      for (const s of strokes) {
        o.globalCompositeOperation = s.r > 0 ? 'source-over' : 'destination-out';
        o.strokeStyle = `rgba(${PAINT_TINT[k]},1)`;
        o.lineWidth = Math.max(2, map.radiusInFrame(s.r) * w * 2);
        o.beginPath();
        s.pts.forEach(([x, y], i) => { const [u, v] = map.toFrame(x, y); if (i) o.lineTo(u * w, v * h); else { o.moveTo(u * w, v * h); o.lineTo(u * w + 0.01, v * h); } });
        o.stroke();
      }
      g.globalAlpha = painting.kind === k ? 0.55 : 0.3;
      g.drawImage(off, 0, 0, w, h);
      g.globalAlpha = 1;
    }
  };
  useEffect(draw);
  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  void tick;
  const at = (e: ReactPointerEvent<HTMLCanvasElement>): [number, number] => {
    const r = e.currentTarget.getBoundingClientRect();
    return map.toPicture((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
  };
  const active = Boolean(painting.kind);
  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      data-testid="qt-paint-layer"
      className={cn('absolute inset-0 h-full w-full rounded-2xl', active ? 'cursor-crosshair touch-none' : 'pointer-events-none')}
      onPointerDown={(e) => {
        if (!painting.kind) return;
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const r = BRUSHES[painting.brush] * (painting.erase ? -1.6 : 1);
        live.current = { r, pts: [at(e)] };
        draw();
      }}
      onPointerMove={(e) => {
        if (!live.current) return;
        e.stopPropagation();
        live.current.pts.push(at(e));
        draw();
      }}
      onPointerUp={(e) => {
        const s = live.current;
        live.current = null;
        if (!s || !painting.kind) return;
        e.stopPropagation();
        const next = regions.map((g) => (g.k === painting.kind ? { ...g, strokes: [...g.strokes, s] } : g));
        const k = painting.kind;
        onPaint(encodePaint(next.some((g) => g.k === k) ? next : [...next, { k, strokes: [s], ...(PAINT_ROOT[k] ? { root: PAINT_ROOT[k] } : {}) }]));
      }}
      onPointerCancel={() => { live.current = null; draw(); }}
    />
  );
}
